import {ADMIN_PERMISSIONS} from './permissions.constant'

export const ROUTES = {
    // auth routes
    SIGN_IN: '/sign-in',
    FORGOT_PASSWORD: '/forgot-password',
    SET_NEW_PASSWORD: '/set-new-password',
    CREATE_PASSWORD: '/create-new-password',
    EXPIRED_LINK: '/expired-link',
    // protected routes
    USER_MANAGEMENT: '/users-management',
    USER_DETAILS: '/users-management/:id',
    ADMIN_MANAGEMENT: '/admins-management',
    ADMIN_DETAILS: '/admins-management/:id',
    CLUBS_MANAGEMENT: '/clubs-management',
    CLUB_DETAILS: '/clubs-management/:id',
    CLUB_EDIT: '/clubs-management/:id/edit',
    CLUBS_POSTS_MANAGEMENT: '/clubs-posts-management',
    CLUB_POSTS_BY_CLUB: '/clubs-posts-management/:clubId',
    CLUB_POST_NEW: '/clubs-posts-management/:clubId/post/new',
    CLUB_POST_DETAILS: '/clubs-posts-management/:clubId/post/:postId',
    CLUB_POST_EDIT: '/clubs-posts-management/:clubId/post/:postId/edit',
    EVENTS_MANAGEMENT: '/events-management',
    EVENT_NEW: '/events-management/new',
    EVENT_DETAILS: '/events-management/:eventId',
    EVENT_EDIT: '/events-management/:eventId/edit',
    COMPETITION_EVENTS_MANAGEMENT: '/competition-events',
    COMPETITION_EVENT_NEW: '/competition-events/new',
    COMPETITION_EVENT_DETAILS: '/competition-events/:slug',
    // Build the minimum SuperAdmin result inbox + registration roster. Nested under
    // COMPETITION_EVENT_DETAILS the same way CLUB_POSTS_BY_CLUB/CLUB_POST_DETAILS nest under a
    // parent id below - SuperAdmin-only for now (kept out of ADMIN_ALLOWED_ROUTES_BY_PERMISSIONS,
    // same treatment as COMPETITION_EVENT_DETAILS itself).
    COMPETITION_EVENT_RESULT_INBOX: '/competition-events/:slug/result-submissions',
    COMPETITION_EVENT_ERGRACE_IMPORT: '/competition-events/:slug/ergrace-import',
    COMPETITION_EVENT_RELAY_RESULTS: '/competition-events/:slug/relay-results',
    COMPETITION_EVENT_RESULT_INBOX_DETAILS: '/competition-events/:slug/result-submissions/:id',
    COMPETITION_EVENT_REGISTRATIONS: '/competition-events/:slug/registrations',
    COMPETITION_EVENT_REGISTRATION_DETAILS: '/competition-events/:slug/registrations/:id',
    COMPETITION_EVENT_AFFILIATION_QUEUE: '/competition-events/:slug/affiliation-queue',
    // T-478.2: SuperAdmin team links - send or re-send a club's team-manager link, open its manager page, link a school licence.
    COMPETITION_EVENT_TEAM_LINKS: '/competition-events/:slug/team-links',
    // Aligner N2 (2026-08-08): the attempts READ seam did not exist anywhere on the backend -
    // only verify/unpublish/correct PATCHes - so BLE/app-published attempts were invisible in
    // this admin panel. Same SuperAdmin-only/kept-out-of-ADMIN_ALLOWED_ROUTES_BY_PERMISSIONS
    // treatment as COMPETITION_EVENT_RESULT_INBOX above.
    COMPETITION_EVENT_ATTEMPTS: '/competition-events/:slug/attempts',
    COMPETITION_EVENT_WEIGH_INS: '/competition-events/:slug/weigh-ins',
    WORKOUTS_MANAGEMENT: '/workouts-management',
    WORKOUT_LOGS_MANAGEMENT: '/workout-logs-management',
    WORKOUT_CREATE: '/workouts-management/new',
    WORKOUT_DETAILS: '/workouts-management/:id',
    WORKOUT_EDIT: '/workouts-management/:id/edit',
    COMMENTS_MANAGEMENT: '/comments-management',
    COMMENTS_BY_TYPE: '/comments-management/:type/:id',
    REPORTS_MANAGEMENT: '/reports-management',
    BLOCKED_USER_MANAGEMENT: '/reports-management/blockedUsers/:userId',
    DASHBOARD: '/dashboard',
    PROGRAMS_MANAGEMENT: '/programs-management',
    PROGRAMS_CREATE: '/programs-management/new',
    PROGRAMS_DETAILS: '/programs-management/:id',
    PROGRAMS_EDIT: '/programs-management/:id/edit',
    CATEGORIES_MANAGEMENT: '/categories-management',
    FEATURED_MANAGEMENT: '/featured-management',
    GUIDE_WATTS_ZONES: '/guide-watts-zones',
    // external routes
    TERM_OF_USE: '/term-of-use',
    PRIVACY_POLICY: '/privacy-policy',
    GREETING: '/greeting',
    // Deep-link fallback routes — these are the paths the Flutter app
    // claims via Universal/App Links (see AndroidManifest + AASA file at
    // /.well-known/apple-app-site-association). When a desktop user (or
    // anyone without the app installed) hits these URLs, the React Router
    // should render GreetingLanding (download CTA) — NOT punt them to
    // admin sign-in.
    //
    // The base prefix versions (without :id) are used by Layout to detect
    // "this is a deep-link path, use ExternalRoutesLayout (no admin chrome)".
    // The :id / :type forms are used by externalRoutes config for React
    // Router pattern matching.
    DEEP_LINK_WORKOUT_PREFIX: '/workout',
    DEEP_LINK_WORKOUT: '/workout/:id',
    DEEP_LINK_PROGRAM_PREFIX: '/program',
    DEEP_LINK_PROGRAM: '/program/:id',
    DEEP_LINK_CLUB_PREFIX: '/club',
    DEEP_LINK_CLUB: '/club/:id',
    DEEP_LINK_EVENT_PREFIX: '/event',
    DEEP_LINK_EVENT: '/event/:id',
    DEEP_LINK_PROFILE_PREFIX: '/profile',
    DEEP_LINK_PROFILE: '/profile/:id',
    DEEP_LINK_SHARE_PREFIX: '/share',
    DEEP_LINK_SHARE: '/share/*',
    DEEP_LINK_RESET_PASSWORD: '/auth/reset-password',
}

export const ADMIN_ALLOWED_ROUTES_BY_PERMISSIONS = [
    {key: ADMIN_PERMISSIONS.CLUB_MEMBERS, path: ROUTES.CLUBS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.CLUB_MEMBERS, path: ROUTES.CLUB_DETAILS},
    {key: ADMIN_PERMISSIONS.CLUB_POSTS, path: ROUTES.CLUBS_POSTS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.CLUB_POSTS, path: ROUTES.CLUB_POSTS_BY_CLUB},
    {key: ADMIN_PERMISSIONS.CLUB_POSTS, path: ROUTES.CLUB_POST_NEW},
    {key: ADMIN_PERMISSIONS.CLUB_POSTS, path: ROUTES.CLUB_POST_DETAILS},
    {key: ADMIN_PERMISSIONS.CLUB_POSTS, path: ROUTES.CLUB_POST_EDIT},
    {key: ADMIN_PERMISSIONS.EVENTS, path: ROUTES.EVENTS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.EVENTS, path: ROUTES.EVENT_DETAILS},
    {key: ADMIN_PERMISSIONS.EVENTS, path: ROUTES.EVENT_EDIT},
    {key: ADMIN_PERMISSIONS.EVENTS, path: ROUTES.EVENT_NEW},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.WORKOUTS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.WORKOUT_LOGS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.WORKOUT_CREATE},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.WORKOUT_DETAILS},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.WORKOUT_EDIT},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.PROGRAMS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.PROGRAMS_CREATE},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.PROGRAMS_DETAILS},
    {key: ADMIN_PERMISSIONS.WORKOUTS, path: ROUTES.PROGRAMS_EDIT},
    {key: ADMIN_PERMISSIONS.COMMENTS, path: ROUTES.COMMENTS_MANAGEMENT},
    {key: ADMIN_PERMISSIONS.COMMENTS, path: ROUTES.COMMENTS_BY_TYPE},
]

export const COMMON_ALLOWED_ADMIN_ROUTES = [{key: null, path: ROUTES.DASHBOARD}]
